import { useColorModeValue } from '@/components/ui/color-mode'
import { uit } from '@/theme'
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { FiBell, FiCheck } from 'react-icons/fi'

/**
 * Notifications Menu Component
 * Modern dropdown with notifications list and advanced styling
 * Uses centralized UI constants from theme/foundations.js
 */
export function NotificationsMenu() {
  const menuBg = useColorModeValue('white', 'gray.900')
  const headerBg = useColorModeValue('#FAFBFC', 'gray.800')
  const borderColor = useColorModeValue('#DFE1E6', 'gray.700')
  const unreadBg = useColorModeValue('#DEEBFF', 'brand.900')
  const badgeBg = useColorModeValue('#0052CC', '#0052CC')
  const badgeShadow = useColorModeValue('0 2px 8px rgba(0, 82, 204, 0.3)', '0 2px 8px rgba(0, 82, 204, 0.5)')

  // TODO: Replace with actual notifications from API
  const notifications = [
    { id: 1, title: 'New lead assigned', time: '5 min ago', unread: true },
    { id: 2, title: 'Property updated', time: '1 hour ago', unread: true },
    { id: 3, title: 'Deal closed', time: '2 hours ago', unread: false },
  ]

  const unreadCount = notifications.filter((n) => n.unread).length

  const handleMarkAllAsRead = () => {
    // TODO: Implement mark all as read API call
  }

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <IconButton
          variant="ghost"
          aria-label="Notifications"
          size="md"
          borderRadius="md"
          transition={uit.animations.transition.base}
          position="relative"
        >
          <Box position="relative">
            <Box as={ FiBell } boxSize={uit.menu.notifications.iconSize} />
            {unreadCount > 0 && (
              <Box
                position="absolute"
                top={uit.badge.notification.offset}
                right={uit.badge.notification.offset}
                bg={ badgeBg }
                color="white"
                borderRadius="full"
                fontSize={uit.badge.notification.fontSize}
                fontWeight={uit.badge.notification.fontWeight}
                minW={uit.badge.notification.size}
                h={uit.badge.notification.size}
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow={ badgeShadow }
                border={uit.badge.notification.border}
                borderColor={ useColorModeValue('white', 'gray.900') }
              >
                { unreadCount }
              </Box>
            )}
          </Box>
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            bg={ menuBg }
            minW={uit.menu.notifications.minWidth}
            maxH={uit.menu.notifications.maxHeight}
            overflowY="auto"
            borderRadius="md"
            boxShadow="lg"
            p={0}
            border="1px"
            borderColor={ borderColor }
          >
            {/* Header */}
            <Box
              px={uit.menu.notifications.headerPadding.x}
              py={uit.menu.notifications.headerPadding.y}
              borderBottom="1px"
              borderColor={ borderColor }
              bg={ headerBg }
              borderTopRadius="md"
            >
              <HStack justify="space-between" align="center">
                <HStack spacing={uit.spacing.item}>
                  <Text fontWeight="700" fontSize="lg">Notifications</Text>
                  {unreadCount > 0 && (
                    <Badge
                      bg="warning.500"
                      color="white"
                      borderRadius="full"
                      px={uit.badge.count.padding.x}
                      py={uit.badge.count.padding.y}
                      fontSize={uit.badge.count.fontSize}
                      fontWeight={uit.badge.count.fontWeight}
                    >
                      { unreadCount } new
                    </Badge>
                  )}
                </HStack>
                {unreadCount > 0 && (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={handleMarkAllAsRead}
                    fontSize="xs"
                    fontWeight="600"
                    color="gray.600"
                    _hover={{ bg: useColorModeValue('gray.100', 'gray.800') }}
                  >
                    <HStack spacing={uit.spacing.tight}>
                      <Box as={FiCheck} boxSize="14px" />
                      <span>Mark all read</span>
                    </HStack>
                  </Button>
                )}
              </HStack>
            </Box>

            {/* Notifications List */}
            {notifications.length === 0 ? (
              <Box px={uit.emptyState.padding.x} py={uit.emptyState.padding.y} textAlign="center">
                <Box
                  as={ FiBell }
                  boxSize={uit.menu.notifications.emptyIconSize}
                  mx="auto"
                  mb={4}
                  color="text.tertiary"
                  opacity={0.5}
                />
                <Text color="text.secondary" fontSize="md" fontWeight="600" mb={1}>
                  No notifications yet
                </Text>
                <Text color="text.tertiary" fontSize="sm">
                  We'll notify you when something arrives
                </Text>
              </Box>
            ) : (
              <VStack spacing={ 0 } align="stretch" py={2}>
                {notifications.map((notification, index) => (
                  <Menu.Item
                    key={ notification.id }
                    value={notification.id.toString()}
                    py={uit.menu.notifications.itemPadding.y}
                    px={uit.menu.notifications.itemPadding.x}
                    borderRadius="md"
                    mx={2}
                    my={0.5}
                    bg={ notification.unread ? unreadBg : 'transparent' }
                    transition={uit.animations.transition.base}
                    _hover={{
                      bg: useColorModeValue('brand.100', 'brand.800'),
                    }}
                    cursor="pointer"
                    position="relative"
                    borderLeft={ notification.unread ? '4px solid' : '4px solid transparent' }
                    borderColor={ notification.unread ? 'brand.500' : 'transparent' }
                  >
                    <HStack spacing={uit.spacing.group} align="start">
                      {/* Unread Indicator */}
                      {notification.unread && (
                        <Box
                          w={uit.menu.notifications.dotSize}
                          h={uit.menu.notifications.dotSize}
                          borderRadius="full"
                          bg="brand.500"
                          mt={1}
                          flexShrink={0}
                          ring={uit.ring.width.sm}
                          ringColor={ useColorModeValue('brand.200', 'brand.700') }
                        />
                      )}

                      {/* Content */}
                      <VStack align="start" spacing={ 1 } flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight={ notification.unread ? '600' : '500' }
                          lineHeight="1.5"
                          color={ notification.unread ? 'text.primary' : 'text.secondary' }
                        >
                          { notification.title }
                        </Text>
                        <Text
                          fontSize="xs"
                          color="text.tertiary"
                          fontWeight="500"
                          letterSpacing="wide"
                        >
                          { notification.time }
                        </Text>
                      </VStack>
                    </HStack>
                  </Menu.Item>
                ))}
              </VStack>
            )}

            {/* Footer */}
            {notifications.length > 0 && (
              <Box
                px={6}
                py={3}
                borderTop="1px"
                borderColor={borderColor}
                textAlign="center"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  width="full"
                  fontWeight="600"
                  color="brand.500"
                  _hover={{ bg: useColorModeValue('brand.50', 'brand.900') }}
                >
                  View all notifications
                </Button>
              </Box>
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export default NotificationsMenu
